/**
 * Background pattern utilities for elegant museum aesthetic
 * Provides subtle, minimal patterns that enhance without overwhelming
 */

export type PatternType = 
  | 'dots' 
  | 'grid' 
  | 'paper' 
  | 'waves'
  | 'diagonal'
  | 'organic'
  | 'none';

export type PatternIntensity = 'subtle' | 'medium' | 'strong';

/**
 * CSS background pattern generators
 * Returns CSS string for background-image
 */
export const backgroundPatterns = {
  /**
   * Subtle dot pattern - very minimal, elegant
   */
  dots: (color: string = 'rgba(0, 0, 0, 0.03)', size: number = 2, spacing: number = 20) => {
    return `radial-gradient(circle, ${color} ${size}px, transparent ${size}px)`;
  },

  /**
   * Subtle grid pattern - museum-like, professional
   */
  grid: (color: string = 'rgba(0, 0, 0, 0.02)', size: number = 20) => {
    return `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`;
  },

  /**
   * Paper texture effect - very subtle, elegant
   */
  paper: (baseColor: string = '#ffffff', intensity: number = 0.015) => {
    return `linear-gradient(${baseColor} 0%, ${baseColor} 100%),
            radial-gradient(circle at 50% 50%, rgba(0, 0, 0, ${intensity}) 0%, transparent 50%)`;
  },

  /**
   * Wave pattern - organic, flowing
   */
  waves: (color: string = 'rgba(0, 0, 0, 0.02)', frequency: number = 50) => {
    return `repeating-linear-gradient(
      45deg,
      transparent,
      transparent ${frequency}px,
      ${color} ${frequency}px,
      ${color} ${frequency * 2}px
    )`;
  },

  /**
   * Diagonal lines pattern
   */
  diagonal: (color: string = 'rgba(0, 0, 0, 0.02)', angle: number = 45, spacing: number = 20) => {
    return `repeating-linear-gradient(
      ${angle}deg,
      transparent,
      transparent ${spacing}px,
      ${color} ${spacing}px,
      ${color} ${spacing + 1}px
    )`;
  },

  /**
   * Organic shapes pattern - subtle, natural
   */
  organic: (color: string = 'rgba(0, 0, 0, 0.015)') => {
    return `radial-gradient(circle at 20% 50%, ${color} 0%, transparent 50%),
            radial-gradient(circle at 80% 50%, ${color} 0%, transparent 50%),
            radial-gradient(circle at 50% 20%, ${color} 0%, transparent 50%),
            radial-gradient(circle at 50% 80%, ${color} 0%, transparent 50%)`;
  },
};

/**
 * Get pattern color based on theme and intensity
 */
export const getPatternColor = (
  isDark: boolean,
  intensity: PatternIntensity = 'subtle'
): string => {
  const opacityMap = {
    subtle: isDark ? 0.015 : 0.02,
    medium: isDark ? 0.03 : 0.04,
    strong: isDark ? 0.05 : 0.06,
  };
  const opacity = opacityMap[intensity];
  return isDark 
    ? `rgba(255, 255, 255, ${opacity})` 
    : `rgba(0, 0, 0, ${opacity})`;
};

/**
 * Get background pattern styles for a pattern type
 */
export const getPatternStyles = (
  pattern: PatternType,
  themeColors: {
    background: string;
    text: string;
    border?: string;
  },
  intensity: PatternIntensity = 'subtle',
  customColor?: string
): {
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
} => {
  const isDark = themeColors.background.includes('#1') || 
                 themeColors.background.includes('#0') ||
                 (themeColors.background.includes('rgb') && 
                  parseInt(themeColors.background.match(/rgb\(\s*(\d+)/)?.[1] || '255') < 50);
  
  const patternColor = customColor || getPatternColor(isDark, intensity);

  switch (pattern) {
    case 'dots':
      return {
        backgroundImage: `${backgroundPatterns.dots(patternColor, 1.5, 20)}, ${themeColors.background}`,
        backgroundSize: '20px 20px, 100% 100%',
        backgroundPosition: '0 0, 0 0',
      };
    case 'grid':
      return {
        backgroundImage: `${backgroundPatterns.grid(patternColor, 24)}, ${themeColors.background}`,
        backgroundSize: '24px 24px, 100% 100%',
        backgroundPosition: '0 0, 0 0',
      };
    case 'paper':
      const paperIntensity = intensity === 'subtle' ? 0.015 : intensity === 'medium' ? 0.025 : 0.035;
      return {
        backgroundImage: backgroundPatterns.paper(themeColors.background, paperIntensity),
        backgroundSize: '100% 100%, 200% 200%',
        backgroundPosition: '0 0, 50% 50%',
      };
    case 'waves':
      return {
        backgroundImage: `${backgroundPatterns.waves(patternColor, 50)}, ${themeColors.background}`,
        backgroundSize: '100px 100px, 100% 100%',
        backgroundPosition: '0 0, 0 0',
      };
    case 'diagonal':
      return {
        backgroundImage: `${backgroundPatterns.diagonal(patternColor, 45, 20)}, ${themeColors.background}`,
        backgroundSize: '40px 40px, 100% 100%',
        backgroundPosition: '0 0, 0 0',
      };
    case 'organic':
      return {
        backgroundImage: `${backgroundPatterns.organic(patternColor)}, ${themeColors.background}`,
        backgroundSize: '200% 200%, 200% 200%, 200% 200%, 200% 200%, 100% 100%',
        backgroundPosition: '0 0, 0 0, 0 0, 0 0, 0 0',
      };
    case 'none':
    default:
      return {};
  }
};

/**
 * Compose multiple patterns together
 */
export const composePatterns = (
  patterns: Array<{
    type: PatternType;
    intensity?: PatternIntensity;
    color?: string;
    opacity?: number;
  }>,
  baseColor: string
): string => {
  const patternStrings = patterns
    .filter(p => p.type !== 'none')
    .map(pattern => {
      const isDark = baseColor.includes('#1') || baseColor.includes('#0');
      const color = pattern.color || getPatternColor(isDark, pattern.intensity || 'subtle');
      const opacity = pattern.opacity || 1;
      const rgbaColor = color.replace(/rgba?\(([^)]+)\)/, (match, values) => {
        const nums = values.split(',').map((v: string) => parseFloat(v.trim()));
        if (nums.length === 3) {
          return `rgba(${nums[0]}, ${nums[1]}, ${nums[2]}, ${opacity})`;
        }
        if (nums.length === 4) {
          return `rgba(${nums[0]}, ${nums[1]}, ${nums[2]}, ${nums[3] * opacity})`;
        }
        return match;
      });

      switch (pattern.type) {
        case 'dots':
          return backgroundPatterns.dots(rgbaColor, 1.5, 20);
        case 'grid':
          return backgroundPatterns.grid(rgbaColor, 24);
        case 'waves':
          return backgroundPatterns.waves(rgbaColor, 50);
        case 'diagonal':
          return backgroundPatterns.diagonal(rgbaColor, 45, 20);
        case 'organic':
          return backgroundPatterns.organic(rgbaColor);
        default:
          return '';
      }
    })
    .filter(Boolean);

  return `${patternStrings.join(', ')}, ${baseColor}`;
};

